/**
 * CohortsWidget — analytics-v2 acquisition-cohort retention (ADS-057 / CORE-962).
 *
 * Marketing-lane widget (PROD data only per plan §6.5). Renders 13 months
 * of acquisition-week (default) or acquisition-month cohorts as a matrix:
 * one row per cohort, one column per period-since-acquisition. Cells are
 * retention rate (active / cohortSize) rendered as `NN.N%`; missing cells
 * (cohort too new to have that offset) render as `—`.
 *
 * Cohort semantic (backend contract): acquisition = buyer's first
 * *successful* purchase — an order that reached `Delivered`. Cancelled
 * / failed orders are excluded. `Delivered → Refunded` still counts
 * (retention measures "buyer reached delivery," not revenue-net-of-
 * refunds). Offset 0 is always 100% by definition.
 *
 * The shell (AnalyticsV2Widget) owns PROD badge + attribution caption
 * + degradation banner + rampUpNote. The window selector is suppressed
 * because cohorts doesn't have a time-window dimension — a granularity
 * `<select>` (weekly | monthly) is passed via `extraControls` instead.
 *
 * Snapshot semantics: the backend serves either a freshly-computed
 * snapshot or a cached snapshot up to 1 hour old (throttle). We surface
 * `snapshotAt` in the caption so the operator knows the data's actual
 * computation time (may lag `lastUpdated` slightly).
 */

import { useMemo, useState } from 'react';
import { getAnalyticsCohorts } from '@/api/client';
import { formatCount, formatDateTime, formatRatioAsPercent } from '@/utils/format';
import { degradedSourceLabels } from '@/utils/sourceStatuses';
import { useAnalyticsV2Fetch } from '@/hooks/useAnalyticsV2Fetch';
import { AnalyticsV2Widget } from './AnalyticsV2Widget';
import { AnalyticsV2ErrorBoundary } from './AnalyticsV2ErrorBoundary';
import type { CohortGranularity, CohortRow } from '@/types/api';

const GRANULARITY_OPTIONS: readonly { value: CohortGranularity; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const DESCRIPTION = (
  <>
    <p>
      <strong>Buyer retention grouped by acquisition period.</strong> Each
      row is a <em>cohort</em> — all buyers whose first successful purchase
      (order that reached <code>Delivered</code>) happened in that week or
      month.
    </p>
    <p>
      Columns <code>P0</code>, <code>P1</code>, <code>P2</code>, ... are{' '}
      <em>periods since acquisition</em>. <code>P0</code> is always
      100% by definition (the cohort&apos;s first purchase). Later{' '}
      <code>Pn</code> cells show what percentage of that cohort made
      another successful purchase during period <code>n</code>.
    </p>
    <p>
      <code>Size</code> is the number of buyers in the cohort. Cells that
      show <code>—</code> are periods the cohort hasn&apos;t reached yet
      (too new).
    </p>
    <p>
      Reading tip: with weekly granularity, <code>P4</code> of a cohort
      acquired on 2026-03-30 means &quot;% of buyers first delivered in
      the week of 2026-03-30 who made another delivered purchase 4 weeks
      later (~2026-04-27).&quot;
    </p>
  </>
);

const RAMP_UP_NOTE = (
  <>
    <strong>Early window — informational only.</strong> Retention curves
    stabilise as the platform accumulates enough buyers per cohort for
    percentages to be meaningful. During the current low-volume period a
    single repeat purchase can swing a cohort&apos;s rate by tens of points.
    Use this to spot obvious pipe failures (empty cohorts, no retention
    at all), not to draw conclusions about product-market fit.
  </>
);

/**
 * Compute the maximum `periodsAgo` seen across all cohorts. Column headers
 * are built by iterating `[0..maxPeriod]` — older cohorts fill more columns,
 * newer cohorts leave the tail empty (rendered as `—`).
 */
function maxPeriodsAgo(cohorts: readonly CohortRow[]): number {
  let max = 0;
  for (const c of cohorts) {
    for (const o of c.offsets) {
      if (o.periodsAgo > max) max = o.periodsAgo;
    }
  }
  return max;
}

/**
 * Index offsets by `periodsAgo` for O(1) cell lookup during render.
 * Backend orders offsets by periodsAgo ascending, but the map insulates
 * us from that promise being reordered later.
 */
function indexOffsets(cohort: CohortRow): Map<number, number> {
  const map = new Map<number, number>();
  for (const o of cohort.offsets) {
    map.set(o.periodsAgo, o.retentionRate);
  }
  return map;
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

/**
 * Compute the calendar-date label for the period at `offset` from a cohort's
 * acquisition period. Used as the per-cell `title` tooltip so hovering a P4
 * cell reveals which specific week / month that column refers to.
 *
 * Weekly: cohortPeriod is `YYYY-MM-DD` (Monday of the week). Offset N → add
 * N × 7 days. Returned as `Week of YYYY-MM-DD`.
 *
 * Monthly: cohortPeriod is `YYYY-MM`. Offset N → add N months, carrying to
 * next year as needed. Returned as `YYYY-MM`.
 *
 * All date math is in UTC to avoid DST / local-timezone off-by-one on the
 * boundary — matches the backend's UTC assumption (GA4 property is fixed
 * UTC per reference-ga4-property-utc).
 */
export function offsetPeriodLabel(
  cohortPeriod: string,
  granularity: CohortGranularity,
  offset: number,
): string {
  if (granularity === 'weekly') {
    const [y, m, d] = cohortPeriod.split('-').map(Number);
    // Fallback to raw cohortPeriod if the backend ever ships a shape we
    // don't expect (ISO with time, empty, non-numeric month, blank inner
    // segment). The m >= 1 && d >= 1 checks catch inputs like "2026--30"
    // where Number("") coerces to 0 and would otherwise silently shift the
    // date one month back via Date.UTC's negative-month-index normalisation.
    if (
      !Number.isFinite(y) ||
      !Number.isFinite(m) ||
      !Number.isFinite(d) ||
      m < 1 ||
      d < 1
    ) {
      return cohortPeriod;
    }
    const date = new Date(Date.UTC(y, m - 1, d));
    date.setUTCDate(date.getUTCDate() + offset * 7);
    return `Week of ${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
  }
  const [y, m] = cohortPeriod.split('-').map(Number);
  // Same rationale as above — m >= 1 catches "2026-" where Number("")
  // coerces to 0 and would shift the month arithmetic backward.
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1) {
    return cohortPeriod;
  }
  const totalMonths = y * 12 + (m - 1) + offset;
  const newYear = Math.floor(totalMonths / 12);
  const newMonth = (totalMonths % 12) + 1;
  return `${newYear}-${pad2(newMonth)}`;
}

export function CohortsWidget(): JSX.Element {
  // State lives here (not inside the body) so the ErrorBoundary's
  // `resetKey` sees the same value the fetch dep sees — a granularity
  // switch that triggers a re-render also resets any error state.
  const [granularity, setGranularity] = useState<CohortGranularity>('weekly');

  return (
    <AnalyticsV2ErrorBoundary title="Cohorts" resetKey={granularity}>
      <CohortsWidgetBody granularity={granularity} setGranularity={setGranularity} />
    </AnalyticsV2ErrorBoundary>
  );
}

interface CohortsWidgetBodyProps {
  granularity: CohortGranularity;
  setGranularity: (g: CohortGranularity) => void;
}

function CohortsWidgetBody({
  granularity,
  setGranularity,
}: CohortsWidgetBodyProps): JSX.Element {
  const { data, error, isLoading } = useAnalyticsV2Fetch(
    () => getAnalyticsCohorts({ granularity }),
    [granularity],
  );

  const degraded = data === null ? [] : degradedSourceLabels(data.sourceStatuses);

  const maxPeriod = useMemo(() => (data === null ? 0 : maxPeriodsAgo(data.cohorts)), [data]);

  const granularitySelector = (
    <label>
      Granularity
      <select
        className="analytics-v2-widget-select"
        value={granularity}
        onChange={(e) => setGranularity(e.target.value as CohortGranularity)}
      >
        {GRANULARITY_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <AnalyticsV2Widget
      title="Cohorts"
      data={data}
      error={error}
      isLoading={isLoading}
      degradedSources={degraded}
      rampUpNote={RAMP_UP_NOTE}
      description={DESCRIPTION}
      extraControls={granularitySelector}
    >
      {data !== null && (
        <>
          <p className="cohorts-widget-caption">
            Snapshot computed {formatDateTime(data.snapshotAt)} (served up to 1h from cache).
          </p>

          {data.cohorts.length === 0 ? (
            degraded.length > 0 ? (
              <p className="empty-state" role="status">
                No cohort data — some sources unavailable (see banner above).
              </p>
            ) : (
              <p className="empty-state" role="status">
                No cohorts in the last 13 months.
              </p>
            )
          ) : (
            <div className="table-wrap">
              <table className="cohorts-widget-table">
                <thead>
                  <tr>
                    <th scope="col">Cohort</th>
                    <th scope="col" className="cohorts-widget-num">Size</th>
                    {Array.from({ length: maxPeriod + 1 }, (_, p) => (
                      <th key={p} scope="col" className="cohorts-widget-num">
                        P{p}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.cohorts.map((cohort) => {
                    const offsetsByPeriod = indexOffsets(cohort);
                    return (
                      <tr key={cohort.cohortPeriod}>
                        <th scope="row">{cohort.cohortPeriod}</th>
                        <td className="cohorts-widget-num">{formatCount(cohort.size)}</td>
                        {Array.from({ length: maxPeriod + 1 }, (_, p) => {
                          const rate = offsetsByPeriod.get(p);
                          // Only cells with data get the date tooltip. Empty
                          // cells ("—") are periods the cohort hasn't reached
                          // yet; a "Week of ..." tooltip on a dash reads as
                          // "data exists" which contradicts the visible dash.
                          const hasData = rate !== undefined;
                          return (
                            <td
                              key={p}
                              className="cohorts-widget-num"
                              title={
                                hasData
                                  ? offsetPeriodLabel(cohort.cohortPeriod, granularity, p)
                                  : undefined
                              }
                            >
                              {formatRatioAsPercent(rate ?? null)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </AnalyticsV2Widget>
  );
}
